import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useV3Window } from "@/v3/useV3Window"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, zIndex, topOffset, style, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    style={{ ...(zIndex ? { zIndex } : {}), ...style, ...(topOffset != null ? { top: topOffset } : {}) }}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// `topOffset` (Modal UI v2) — when set, keeps the app header/sub-header
// visible+interactive above the overlay by pinning the overlay/content to
// start below that height instead of covering the full viewport, and
// re-anchors the content near the top instead of dead-center.
// The height cap is a percentage of the viewport box (this content is position:
// fixed, so 100% is the viewport) instead of a raw 100vh, so the dialog finishes
// just above the bottom of the screen even when the page is zoomed — a percentage
// has no viewport units in it (see utils/modalViewport.js).
// The widths the Tailwind max-width classes stand for, so a dialog can tell the V3 shell how wide it wants to be (it never changes what the dialog looks like).
const V3_MAX_WIDTHS = { "max-w-xs": 320, "max-w-sm": 384, "max-w-md": 448, "max-w-lg": 512, "max-w-xl": 576, "max-w-2xl": 672, "max-w-3xl": 768, "max-w-4xl": 896, "max-w-5xl": 1024, "max-w-6xl": 1152, "max-w-7xl": 1280 }
const v3DialogWidth = (classNames) => {
  const tokens = String(classNames || "").split(/\s+/).map((token) => token.replace(/^[a-z]+:/, ""))
  const named = [...tokens].reverse().find((token) => V3_MAX_WIDTHS[token])
  if (named) return V3_MAX_WIDTHS[named]
  const px = tokens.map((token) => token.match(/^max-w-\[(\d+)px\]$/)).find(Boolean)
  if (px) return Number(px[1])
  const vw = tokens.map((token) => token.match(/^(?:max-w|w)-\[(\d+)vw\]$/)).find(Boolean)
  if (vw && typeof window !== "undefined") return Math.round((window.innerWidth * Number(vw[1])) / 100)
  return 512
}

const DialogContent = React.forwardRef(({ className, children, hideCloseButton, overlayClassName, zIndex, topOffset, style, ...props }, ref) => {
  // Inside UI V3 a dialog is a V3 window: it sits in the content area between the side bars with the blurred glass behind it, and it tells the shell how wide it wants
  // to be (see useV3Window). The dialog's own contents are unchanged. Outside UI V3 this does nothing and the dialog is exactly as before.
  const v3 = useV3Window(true, v3DialogWidth(className), { single: true })
  if (v3.active) {
    return (
      <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40" style={{ background: "transparent" }} />
        <div className={v3.className} {...v3.attrs} style={v3.style} data-v3-adopted="dialog">
          <DialogPrimitive.Content
            ref={ref}
            className={cn("relative grid w-full gap-4 border bg-white p-6 shadow-lg sm:rounded-lg overflow-y-auto", className)}
            style={style}
            {...props}>
            {children}
            {!hideCloseButton && (
              <DialogPrimitive.Close
                className="absolute right-4 top-4 rounded-full w-8 h-8 flex items-center justify-center bg-gray-100 transition-all focus:outline-none disabled:pointer-events-none">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    )
  }
  const topOffsetStyle = topOffset != null ? {
    top: `calc(${topOffset}px + 16px)`,
    left: '50%',
    transform: 'translateX(-50%)',
    maxHeight: `calc(100% - ${topOffset}px - 32px)`,
  } : {};
  return (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} zIndex={zIndex} topOffset={topOffset} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[90vh] overflow-y-auto",
        className
      )}
      style={{ ...(zIndex ? { zIndex } : {}), ...style, ...topOffsetStyle }}
      {...props}>
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-full w-8 h-8 flex items-center justify-center bg-gray-100 opacity-80 ring-offset-background transition-all hover:opacity-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
